import React, { FormEvent, ReactElement, useState, useEffect } from "react";
import styles from "./Login.module.css";
import Alert from "../../Alert/Alert";
import SendToast from "../../Toast/Toast";
import Text from "../../Text/Text";
import LinkButton from "../../LinkButton/LinkButton";
import InputContainer from "../../InputContainer/InputContainer";
import Modal from "../../Modal/Modal";
import MFASetup from "../MFASetup/MFASetup";
import VerifyLogin from "../VerifyLogin/VerifyLogin";
import Button from "../../Button/Button";
import nrLogo from "./NRLogoLarge.png";
import { useHistory, RouteComponentProps } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useMutation } from "react-query";
import RequestAccessOrPermissionModal from "./RequestAccessOrPermissionModal";

interface LoginProps extends RouteComponentProps<any> {
  railAuthApiUrl: string;
  railAuthWebUrl: string;
  clientId: string;
  railAuthGroups?: Array<string>;
  logoStyle?: React.CSSProperties;
  headerText?: string;
  headerStyle?: React.CSSProperties;
  signInButtonStyle?: React.CSSProperties;
  customAxiosInstance?: any;
}

const Login: React.FC<LoginProps> = (props): ReactElement => {
  const {
    logoStyle = {},
    headerText = "",
    headerStyle = {},
    signInButtonStyle = {},
    railAuthGroups = [],
    customAxiosInstance,
  } = props;
  const axiosInstance = customAxiosInstance || axios;
  const history = useHistory();
  const searchParams = new URLSearchParams(props.location.search);
  const error = searchParams.get("error");
  const status = searchParams.get("status");
  const showErrorAlert = error === "incorrect_password";
  const showAccountDeletedAlert = status === "account_deleted";
  const showSessionExpiredToast = error === "session_expired";

  const { register, handleSubmit, errors, reset } = useForm();
  const [requestPermissionModalIsShown, setRequestPermissionModalIsShown] = useState(false);
  const [mfaSetupModalIsShown, setMfaSetupModalIsShown] = useState(false);
  const [verifyLoginModalIsShown, setVerifyLoginModalIsShown] = useState(false);
  const [firstLogin, setFirstLogin] = useState(false);
  const [session, setSession] = useState();
  const [loginResponse, setLoginResponse] = useState();
  const [secretCode, setSecretCode] = useState();

  const [loginUser] = useMutation(
    (user) => axiosInstance.post("login", user, { baseURL: `${props.railAuthApiUrl}/api/` }),
    {
      onSuccess: (response: any) => {
        if (response.data.SecretCode) {
          setSecretCode(response.data.SecretCode);
          setSession(response.data.Session);
          setMfaSetupModalIsShown(true);
        } else if (response.data.userIdForSRP) {
          setLoginResponse(response.data);
          setVerifyLoginModalIsShown(true);
        } else {
          localStorage.setItem("authenticated", JSON.stringify(response.data.accessToken));
          localStorage.setItem("refreshToken", JSON.stringify(response.data.refreshToken));
          localStorage.setItem("user", JSON.stringify(response.data.user));
          history.push("/");
        }
      },
      onError: (error: any) => {
        if (error.response.data.message === "User is not confirmed.") {
          SendToast("User not confirmed. Please complete your registration.", "error");
          redirectToUrl(
            `${props.railAuthWebUrl}/complete-registration/${
              JSON.parse(error.config.data).username
            }/${props.clientId}`
          );
        } else if (
          error.response.data.message.includes("not in the required groups") &&
          !error.response.data.message.includes("@")
        ) {
          // Error msg only applicable to NR AD users (thus the check to see
          // if the error message includes @ - which only AWS Cognito users would have)
          setRequestPermissionModalIsShown(true);
        } else if (error.response.data.message.includes("verify their email")) {
          SendToast(
            "Your email address requires verification to access this app. Please check your inbox for further instructions.",
            "error"
          );
        } else if (error.response.data.message === "Too many login attempts") {
          SendToast("Too many login attempts, please try again later.", "error");
        } else {
          SendToast("Incorrect login credentials. Please try again", "error");
        }
      },
    }
  );

  useEffect(() => {
    if (showSessionExpiredToast) {
      SendToast("Your session has expired. Please log in.", "error");
    }
  }, []);

  const onSubmit = async (data: any) => {
    const loginData: any = {
      username: data.username,
      password: data.password,
      groups: railAuthGroups,
    };

    loginUser(loginData);
    reset();
  };

  const redirectToUrl = (url: string) => {
    window.location.href = url;
  };

  const setupComplete = (e: FormEvent) => {
    e.preventDefault();
    setMfaSetupModalIsShown(false);
    setVerifyLoginModalIsShown(true);
    setFirstLogin(true);
  };

  const verifySetupComplete = () => {
    setSession(undefined);
    setSecretCode(undefined);
    setLoginResponse(undefined);
    setFirstLogin(false);
    setVerifyLoginModalIsShown(false);
  };

  return (
    <div className={styles.login_panel}>
      <div className={styles.login_form}>
        <div className={styles.login_logo}>
          <img src={nrLogo} alt="Network Rail" style={logoStyle} />
        </div>
        {showErrorAlert && (
          <Alert
            color="blue"
            title="You have been signed out"
            message="You have been signed out as you entered in the wrong password too many times. Please reset your password or try again later."
          />
        )}
        {showAccountDeletedAlert && (
          <Alert
            color="blue"
            title="You have been signed out"
            message="You have been signed out as you have deleted your RailAuth account. If you still require a RailAuth account please sign up for a new RailAuth account."
          />
        )}
        <div className={styles.login_header}>
          <h2 className={styles.login_title} style={headerStyle}>
            {headerText}
          </h2>
        </div>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_inputs}>
            <InputContainer
              key="username"
              forLabel="username"
              required={true}
              exampleText="Please enter the email address you used to sign up to RailAuth. Network Rail users please use your username"
              errorText={errors.username ? errors.username.message : null}
              labelText="Username or Email Address"
            >
              <input
                name="username"
                className={errors.username ? styles.error : ""}
                id="username"
                type="text"
                ref={register({
                  required: {
                    value: true,
                    message: "Must not be blank",
                  },
                })}
              />
            </InputContainer>
            <div className={styles.login_forgotPassword}>
              <LinkButton
                name="forgotPassword"
                type="button"
                style={{
                  fontSize: "14px",
                  color: "#37519D",
                }}
                onClick={() => {
                  redirectToUrl(`${props.railAuthWebUrl}/forgot-password/${props.clientId}`);
                }}
              >
                Forgot password?
              </LinkButton>
            </div>
            <InputContainer
              key="password"
              forLabel="password"
              required={true}
              errorText={errors.password ? errors.password.message : null}
              labelText="Password"
            >
              <input
                name="password"
                className={errors["password"] ? styles.error : ""}
                id="password"
                type="password"
                ref={register({
                  required: {
                    value: true,
                    message: "Must not be blank",
                  },
                })}
              />
            </InputContainer>
          </div>
          <div className={styles.form_buttons}>
            <Button
              color="blue"
              text="Sign In"
              name="login"
              type="submit"
              style={{
                borderRadius: "20px",
                justifySelf: "center",
                backgroundColor: "#37519D",
                height: "44px",
                width: "299px",
                fontSize: "16px",
                fontWeight: "normal",
                marginTop: "34px",
                marginBottom: "10px",
                ...signInButtonStyle,
              }}
            />
          </div>
        </form>
        <div className={styles.login_signUp}>
          <Text type="body" wrapper="span" size="14px">
            Not a member? {""}
            <LinkButton
              name="signUp"
              style={{
                fontSize: "14px",
                padding: 1,
                color: "#37519D",
              }}
              onClick={() => {
                redirectToUrl(`${props.railAuthWebUrl}/register/${props.clientId}`);
              }}
            >
              Sign up now
            </LinkButton>
          </Text>
        </div>
      </div>
      <RequestAccessOrPermissionModal
        show={requestPermissionModalIsShown}
        onClose={() => setRequestPermissionModalIsShown(false)}
      />
      <Modal
        hideControls
        show={mfaSetupModalIsShown}
        onClose={() => {
          setMfaSetupModalIsShown(false);
        }}
        id="Modal"
        type="confirm"
        describedBy="MFASetup"
        labelledBy="MFASetup"
        style={{ top: 50 }}
      >
        <MFASetup
          secretCode={secretCode}
          logoStyle={{
            alignSelf: "center",
          }}
          headerStyle={{ color: signInButtonStyle.backgroundColor }}
          signInButtonStyle={signInButtonStyle}
          setupComplete={setupComplete}
          close={() => {
            setMfaSetupModalIsShown(false);
          }}
        />
      </Modal>
      <Modal
        hideControls
        show={verifyLoginModalIsShown}
        onClose={() => {
          setVerifyLoginModalIsShown(false);
        }}
        id="Modal"
        type="confirm"
        describedBy="VerifyMFA"
        labelledBy="VerifyMFA"
        style={{ top: 100 }}
      >
        <VerifyLogin
          session={session}
          loginResponse={loginResponse}
          railAuthApiUrl={props.railAuthApiUrl}
          logoStyle={{
            alignSelf: "center",
          }}
          headerStyle={{ color: signInButtonStyle.backgroundColor }}
          signInButtonStyle={signInButtonStyle}
          firstLogin={firstLogin}
          verifySetupComplete={verifySetupComplete}
          close={() => {
            setVerifyLoginModalIsShown(false);
          }}
          back={() => {
            setMfaSetupModalIsShown(true);
            setVerifyLoginModalIsShown(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default Login;
